/**
 * useMcpAdmin — thin re-export bundle for the System → MCP page.
 *
 * The list hooks live in `useEdrData.ts` (they share the same EDR API
 * client and 404 tolerance). The revoke mutation does an optimistic
 * cache update and rolls back on failure. When the P2 endpoint isn't
 * live yet (404), we surface "Not yet available" via toast and revert.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { edrPatch, EdrApiError } from "@/services/edrApi";
import type { McpClientsResponseDTO } from "@/types/edrApi";

export { useMcpClients, useMcpTools } from "./useEdrData";

interface RevokeMcpClientInput {
  id: string;
  displayName?: string;
}

const CLIENTS_QUERY_KEY = ["edr", "admin", "mcp", "clients"] as const;

export function useRevokeMcpClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: RevokeMcpClientInput) => {
      await edrPatch(
        `/api/v1/connectors/edr/admin/mcp/clients/${encodeURIComponent(id)}`,
        { status: "revoked" },
      );
    },
    onMutate: async ({ id }) => {
      // Optimistically flip the client's status → revoked.
      await qc.cancelQueries({ queryKey: CLIENTS_QUERY_KEY });
      const previous = qc.getQueryData<McpClientsResponseDTO | null>(CLIENTS_QUERY_KEY);
      if (previous?.clients) {
        const next: McpClientsResponseDTO = {
          ...previous,
          clients: previous.clients.map((c) =>
            c.id === id ? { ...c, status: "revoked" } : c,
          ),
        };
        qc.setQueryData(CLIENTS_QUERY_KEY, next);
      }
      return { previous };
    },
    onSuccess: (_data, vars) => {
      toast.success(`Revoked ${vars.displayName ?? vars.id}`);
    },
    onError: (err, vars, ctx) => {
      // Roll back optimistic update.
      if (ctx?.previous !== undefined) {
        qc.setQueryData(CLIENTS_QUERY_KEY, ctx.previous);
      }
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Revoke not yet available", {
          description: `The MCP admin endpoint hasn't shipped yet. Couldn't revoke ${vars.displayName ?? vars.id}.`,
        });
        return;
      }
      const message = err instanceof Error ? err.message : "Revoke failed";
      toast.error(message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
    },
  });
}
